import type { SupportedLocale } from './localization';
import type { PublicLeadInput } from './publicLead';

export const CONTACT_PRODUCT_OPTIONS = [
  { value: 'corporate_website', en: 'Corporate website', es: 'Sitio web corporativo' },
  { value: 'campaign_landing', en: 'Campaign landing page', es: 'Página de aterrizaje de campaña' },
  { value: 'ux_ui', en: 'UX/UI design', es: 'Diseño UX/UI' },
  { value: 'branding', en: 'Brand identity', es: 'Identidad de marca' },
  { value: 'ecommerce', en: 'Ecommerce storefront', es: 'Comercio electrónico' },
  { value: 'saas', en: 'SaaS product website', es: 'Sitio web de producto SaaS' },
  { value: 'other', en: 'Other service', es: 'Otro servicio' },
] as const;

export type ContactProductValue = (typeof CONTACT_PRODUCT_OPTIONS)[number]['value'];

type ContactCopy = {
  fullName: string;
  fullNamePlaceholder: string;
  company: string;
  companyPlaceholder: string;
  phone: string;
  phonePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  product: string;
  productPlaceholder: string;
  need: string;
  needPlaceholder: string;
  budget: string;
  budgetPlaceholder: string;
  privacy: string;
  sending: string;
  success: string;
  error: string;
  phoneInvalid: string;
};

export const CONTACT_FORM_COPY: Record<SupportedLocale, ContactCopy> = {
  es: {
    fullName: 'Nombre completo',
    fullNamePlaceholder: 'Nombre y apellidos',
    company: 'Empresa u organización',
    companyPlaceholder: 'Nombre de la empresa u organización',
    phone: 'Teléfono',
    phonePlaceholder: '+1 000 000 0000',
    email: 'Correo electrónico',
    emailPlaceholder: 'correo@empresa.com',
    product: 'Producto o servicio de interés',
    productPlaceholder: 'Seleccione una opción',
    need: 'Describa su necesidad',
    needPlaceholder:
      'Indique el objetivo del proyecto, el alcance previsto y cualquier requisito relevante para elaborar su propuesta.',
    budget: 'Presupuesto estimado (opcional)',
    budgetPlaceholder: 'Por ejemplo: USD 3.000 – 7.000',
    privacy: 'La información proporcionada se utilizará exclusivamente para elaborar y remitir su propuesta.',
    sending: 'Enviando solicitud...',
    success:
      'Hemos recibido su solicitud. Un integrante del equipo se pondrá en contacto con usted a la mayor brevedad.',
    error: 'No fue posible enviar su solicitud. Por favor, inténtelo de nuevo.',
    phoneInvalid: 'Por favor, ingrese un número de teléfono válido.',
  },
  en: {
    fullName: 'Full name',
    fullNamePlaceholder: 'First and last name',
    company: 'Company or organization',
    companyPlaceholder: 'Company or organization name',
    phone: 'Phone number',
    phonePlaceholder: '+1 000 000 0000',
    email: 'Email address',
    emailPlaceholder: 'name@company.com',
    product: 'Product or service of interest',
    productPlaceholder: 'Please select an option',
    need: 'Describe your requirement',
    needPlaceholder:
      'Please outline the project objective, expected scope, and any relevant requirements for a formal proposal.',
    budget: 'Estimated budget (optional)',
    budgetPlaceholder: 'For example: USD 3,000 – 7,000',
    privacy: 'The information provided will be used solely to prepare and send your proposal.',
    sending: 'Submitting your request...',
    success: 'We have received your request. A member of our team will contact you at the earliest opportunity.',
    error: 'We were unable to send your request. Please try again.',
    phoneInvalid: 'Please enter a valid phone number.',
  },
};

export function productLabel(value: string, locale: SupportedLocale): string {
  const option = CONTACT_PRODUCT_OPTIONS.find((item) => item.value === value);
  return option ? option[locale] : value;
}

export function hasValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function leadFromContactForm(
  formData: FormData,
  options: { source: PublicLeadInput['source']; landingUrl?: string; locale: SupportedLocale }
): PublicLeadInput {
  return {
    name: String(formData.get('name') || ''),
    email: String(formData.get('email') || ''),
    phone: String(formData.get('phone') || ''),
    company: String(formData.get('company') || ''),
    productInterest: String(formData.get('product') || ''),
    message: String(formData.get('need') || ''),
    budget: String(formData.get('budget') || ''),
    source: options.source,
    landingUrl: options.landingUrl,
    locale: options.locale,
  };
}
